import { ToastrService } from 'ngx-toastr';
import { Promotion } from '@model/promotion';
import { Globals } from '@theme/utils/globals';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Component, HostListener, OnInit } from '@angular/core';
import { ValidatorsEnums } from '@theme/enums/validators.enums';
import { EmitterService } from '@service/emitter/emitter.service';
import { DateHelperService } from '@theme/utils/date-helper.service';
import { UploaderService } from '@service/uploader/uploader.service';
import { PromotionService } from '@service/promotion/promotion.service';
import { HomeTypeService } from '@service/home-type/home-type.service';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import Swal from 'sweetalert2/dist/sweetalert2.js';

// Interface pour les types de maisons (TYPE A) - MISE À JOUR
interface MaisonType {
  typeUuid: string;
  typeLabel: string;
  count: number;
  superficie: number;
  montant: number;
  baseActualisation: number; // NOUVEAU CHAMP
  generated: boolean;
}

// Interface pour les maisons dans un niveau - MISE À JOUR
interface MaisonTypeNiveau {
  typeUuid: string;
  typeLabel: string;
  count: number;
  superficie: number;
  montant: number;
  baseActualisation: number; // NOUVEAU CHAMP
}

// Interface pour un niveau d'un bâtiment - MISE À JOUR
interface NiveauBatiment {
  numero: number;
  maisonTypes: MaisonTypeNiveau[];
  expanded?: boolean; // Pour l'affichage accordéon
  // Propriétés temporaires pour l'ajout de nouveaux types
  newMaisonTypeUuid?: string;
  newMaisonTypeLabel?: string;
  newMaisonCount?: number;
  newMaisonSuperficie?: number;
  newMaisonMontant?: number;
  newMaisonBaseActualisation?: number; // NOUVEAU CHAMP
}

// Interface pour les maisons générées dans un bâtiment - MISE À JOUR
interface GeneratedHome {
  uuid?: string;
  numero: string;
  niveau: number;
  typeUuid: string;
  typeLabel: string;
  superficie: number;
  montant: number;
  baseActualisation: number; // NOUVEAU CHAMP
  editable?: boolean; // Pour permettre l'édition du numéro
}

// Interface pour les bâtiments (TYPE B) - pas de changement
interface Batiment {
  libelle: string;
  niveau: number;
  niveaux: NiveauBatiment[];
  generated: boolean;
  homes: GeneratedHome[]; // Maisons du bâtiment
  expanded?: boolean; // Pour l'affichage accordéon
}

@Component({
  selector: 'app-promotion-add',
  templateUrl: './promotion-add.component.html',
  styleUrls: ['./promotion-add.component.scss']
})
export class PromotionAddComponent implements OnInit {

  ESCAPE_KEYCODE = 27;
  title: string = "";
  activeTab: string = "PIÈCES";
  edit: boolean = false;
  promotion: Promotion;
  promotions: Promotion[] = [];
  form: FormGroup;
  pieceForm: FormGroup;
  equipementForm: FormGroup;
  galleryForm: FormGroup;
  videoForm: FormGroup;
  planForm: FormGroup;
  submit: boolean = false;
  required = Globals.required;
  lat = Globals.lat;
  lng = Globals.lng;
  zoom = Globals.zoom;
  canCloseAccordion = false;

  // Propriétés pour TYPE A (maisons) - MISE À JOUR
  maisonTypes: MaisonType[] = [];
  newTypeUuid: string = '';
  newTypeLabel: string = '';
  newTypeCount: number = 1;
  newTypeSuperficie: number = 0;
  newTypeMontant: number = 0;
  newTypeBaseActualisation: number = 0; // NOUVEAU CHAMP
  
  // Propriétés pour la configuration du radical et décompte
  radicalCode: string = ''; // Radical du code (optionnel)
  startNumber: number = 1; // Numéro de départ du décompte
  defaultIlot: number = 1; // Ilot par défaut si pas de radical
  numberingMode: string = 'continue'; // Mode de numérotation : 'continue' ou 'restart'

  // Propriétés pour TYPE B (bâtiments)
  batiments: Batiment[] = [];
  newBuildingLibelle: string = '';
  newBuildingNiveau: number = 1;

  homeTypes: any[] = []; // À remplir avec vos types de maisons depuis le service

  map?: any;
  etatRow = [
    { label: "Inatif", value: "INACTIF" },
    { label: "Actif", value: "ACTIF" }
  ];
  typeRow = [
    { label: "Type A (Le programme de type A correspond aux maisons basses, duplex et triplex (F3, F4, F5, F6).)", value: "TYPE_A" },
    { label: "Type B (Le programme de type B correspond aux immeubles: appartements (F3, F4, F5 et +, F6 duplex, F6 triplex).)", value: "TYPE_B" },
    { label: "Type C (Le programme de type C corespond TYPE A et TYPE B)", value: "TYPE_C" }
  ];

  public galleryFile: any[];
  public planFile: any[];
  ownerSelected: any;

  constructor(
    public modal: NgbActiveModal,
    private formBuild: FormBuilder,
    private promotionService: PromotionService,
    public uploadService: UploaderService,
    private emitter: EmitterService,
    public toastr: ToastrService,
    private homeTypeService: HomeTypeService
  ) {
    this.edit = this.promotionService.edit;
    this.promotion = this.promotionService.getPromotion();
    this.promotionService.getList().subscribe((res) => {
      for (let index = 0; index < res.length; index++) {
        const element = res[index];
        if (this.promotion?.uuid != element.uuid) {
          this.promotions.push(element);
        }
      }
    });
    this.title = (!this.edit) ? "Ajouter un programme" : "Modifier le programme " + this.promotion.libelle;
    this.newForm();
  }

  ngOnInit(): void {
    this.editForm();
    this.loadHomeTypes();
  }

  newForm() {
    this.form = this.formBuild.group({
      uuid: [null],
      id: [null],
      date: [null, [Validators.required]],
      libelle: [null, [Validators.required]],
      type: ["TYPE_A", [Validators.required]],
      etat: ["INACTIF", [Validators.required]],
      superficie: [null, [Validators.required, Validators.min(0)]],
      ville: [null, [Validators.required]],
      commune: [null, [Validators.required]],
      quartier: [null, [Validators.required]],
      owner: [null, [Validators.required]],
      surfaceBati: [0, [Validators.pattern(ValidatorsEnums.decimal)]],
      montantCr: [0, [Validators.pattern(ValidatorsEnums.decimal)]],
      montantCc: [0, [Validators.pattern(ValidatorsEnums.decimal)]],
      montantAdmin: [0, [Validators.pattern(ValidatorsEnums.decimal)]],
      montantVrd: [0, [Validators.pattern(ValidatorsEnums.decimal)]],
      lng: [null],
      lat: [null],
      zoom: [null],
      folderUuid: [null],
      parent: [null],
      isOffre: ["NON", [Validators.required]],
      dateOffre: [null],
      homes: this.formBuild.array([]),
      buildings: this.formBuild.array([]),
      files: this.formBuild.array([]),
      folders: this.formBuild.array([]),
      piece: [null],
      equipement: [null],
      video: [null],
      gallery: [null],
      plan: [null]
    });

    // Gestion des validateurs conditionnels
    this.form.get('isOffre').valueChanges.subscribe(res => {
      if (res === 'OUI') {
        this.form.get('dateOffre').setValidators(Validators.required);
      } else {
        this.form.get('dateOffre').clearValidators();
      }
      this.form.get('dateOffre').updateValueAndValidity();
    });

    this.form.get('type').valueChanges.subscribe(res => {
      if (!this.edit) {
        this.resetData();
      }
    });

    // Initialisation des autres formulaires
    this.pieceForm = this.formBuild.group({
      chambre: [0],
      douche: [0],
      salon: [0],
      cuisine: [0],
      garage: [0],
    });

    this.equipementForm = this.formBuild.group({
      wifi: [false],
      clim: [false],
      securite: [false],
      parking: [false],
      incendie: [false],
      urgence: [false],
      piscine: [false],
      concierge: [false],
      nbPlace: [0],
    });

    this.galleryForm = this.formBuild.group({
      files: [null],
    });

    this.planForm = this.formBuild.group({
      files: [null],
    });

    this.videoForm = this.formBuild.group({
      link: [null],
    });
  }

  editForm() {
    if (this.edit) {
      const data = { ...this.promotionService.getPromotion() };
      this.lat = data.lat ? data.lat : Globals.lat;
      this.lng = data.lng ? data.lng : Globals.lng;
      this.form.get('uuid').setValue(this.promotion?.uuid);
      data.date = DateHelperService.fromJsonDate(data.date);
      this.ownerSelected = {
        photoSrc: data?.owner?.photoSrc,
        title: data?.owner?.searchableTitle,
        detail: data?.owner?.searchableDetail
      };
      this.form.get('owner').setValue(data?.owner?.uuid);
      this.form.patchValue(data);
      if (data.parent) {
        this.form.get('parent').setValue(data?.parent?.uuid);
      }
      this.form.get('uuid').setValue(this.promotion?.uuid);
    }
  }

  // Charger les types de maisons
  loadHomeTypes() {
    this.homeTypeService.getList().subscribe(types => {
      this.homeTypes = types;
    }, error => {
      console.error('Erreur lors du chargement des types de maisons:', error);
    });
  }

  // === MÉTHODES POUR TYPE A (MAISONS) - MISES À JOUR ===
  
  setNewTypeUuid(uuid: string) {
    this.newTypeUuid = uuid;
    // Charger les détails du type sélectionné
    this.loadHomeTypeDetails(uuid).then(homeType => {
      if (homeType) {
        this.newTypeLabel = homeType.libelle || homeType.name;
      }
    });
  }

  // MISE À JOUR : Ajout de la validation du champ baseActualisation
  addMaisonType() {
    if (!this.newTypeUuid || !this.newTypeCount || !this.newTypeSuperficie || !this.newTypeMontant || !this.newTypeBaseActualisation) {
      this.toast('Veuillez remplir tous les champs requis', 'Erreur', 'error');
      return;
    }

    const existingType = this.homeTypes.find(type => type.uuid === this.newTypeUuid);

    const newMaisonType: MaisonType = {
      typeUuid: this.newTypeUuid,
      typeLabel: existingType.libelle,
      count: this.newTypeCount,
      superficie: this.newTypeSuperficie,
      montant: this.newTypeMontant,
      baseActualisation: this.newTypeBaseActualisation, // NOUVEAU CHAMP
      generated: false
    };

    this.maisonTypes.push(newMaisonType);
    this.resetNewMaisonTypeForm();
    this.toast('Type de maison ajouté avec succès', 'Succès', 'success');
  }

  removeMaisonType(index: number) {
    Swal.fire({
      title: 'Confirmation',
      text: 'Êtes-vous sûr de vouloir supprimer ce type de maison ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.maisonTypes.splice(index, 1);
        this.regenerateHomes();
        this.toast('Type de maison supprimé', 'Succès', 'success');
      }
    });
  }

  generateMaisonsByType(typeIndex: number) {
    const maisonType = this.maisonTypes[typeIndex];
    if (!maisonType) return;

    this.generateHomes(maisonType);
    maisonType.generated = true;
    this.toast(`${maisonType.count} maisons générées`, 'Succès', 'success');
  }

  generateAllMaisons() {
    this.maisonTypes.forEach((maisonType, index) => {
      if (!maisonType.generated) {
        this.generateMaisonsByType(index);
      }
    });
  }

  // Méthode pour calculer le prochain numéro de lot disponible
  getNextAvailableLotNumber(): number {
    if (this.numberingMode === 'restart') {
      // Mode restart : toujours commencer avec startNumber
      return this.startNumber;
    }
    
    if (this.home.length === 0) {
      // Aucune maison existante, commencer avec startNumber
      return this.startNumber;
    }
    
    // Mode continue : analyser les numéros existants pour continuer la série
    const existingLots = this.home.controls.map(control => {
      const lotValue = control.get('lot').value;
      if (typeof lotValue === 'string' && this.radicalCode && this.radicalCode.trim() !== '') {
        // Extraire le numéro du radical (ex: "LOT5" -> 5)
        const match = lotValue.match(/\d+$/);
        return match ? parseInt(match[0]) : this.startNumber;
      }
      return typeof lotValue === 'number' ? lotValue : parseInt(lotValue) || this.startNumber;
    });
    
    // Trouver le plus grand numéro et ajouter 1
    const maxNumber = Math.max(...existingLots, this.startNumber - 1);
    return maxNumber + 1;
  }

  // MISE À JOUR : Ajout du champ baseActualisation dans la génération
  generateHomes(maisonType: MaisonType) {
    // Utiliser le prochain numéro disponible selon la configuration actuelle
    let currentLotNumber = this.getNextAvailableLotNumber();
    
    for (let i = 0; i < maisonType.count; i++) {
      // Génération du numéro de lot avec ou sans radical
      let lotFormatted: string;
      if (this.radicalCode && this.radicalCode.trim() !== '') {
        // Avec radical : RADICAL + numéro
        lotFormatted = `${this.radicalCode.trim()}${currentLotNumber}`;
      } else {
        // Sans radical : juste le numéro
        lotFormatted = currentLotNumber.toString();
      }
      
      // Détermination de l'ilot
      let ilotValue: number;
      if (this.radicalCode && this.radicalCode.trim() !== '') {
        // Si radical défini, ilot = numéro du lot
        ilotValue = currentLotNumber;
      } else {
        // Si pas de radical, ilot = defaultIlot (1)
        ilotValue = this.defaultIlot;
      }
      
      this.home.push(
        this.formBuild.group({
          uuid: [null],
          id: [null],
          lot: [lotFormatted, [Validators.required]],
          type: [maisonType.typeUuid, [Validators.required]],
          typeLibelle: [maisonType.typeLabel, [Validators.required]],
          ilot: [ilotValue, [Validators.required, Validators.pattern(ValidatorsEnums.number), Validators.min(0)]],
          montant: [maisonType.montant, [Validators.required]],
          superficie: [maisonType.superficie, [Validators.required]],
          baseActualisation: [maisonType.baseActualisation, [Validators.required]], // NOUVEAU CHAMP
        })
      );
      
      // Incrémenter pour la prochaine maison
      currentLotNumber++;
    }
  }

  regenerateHomes() {
    this.home.clear();
    this.maisonTypes.forEach(maisonType => {
      maisonType.generated = false;
    });
  }

  getTotalMaisons(): number {
    return this.maisonTypes.reduce((total, type) => total + type.count, 0);
  }

  allMaisonsGenerated(): boolean {
    return this.maisonTypes.length > 0 && this.maisonTypes.every(type => type.generated);
  }

  // Méthode pour vérifier si toutes les maisons simples sont générées (TYPE_C)
  canProceedFromMaisonsSimples(): boolean {
    if (this.f.type.value === 'TYPE_C') {
      // Pour TYPE_C, on peut continuer même sans maisons simples si on a des bâtiments
      return this.maisonTypes.length === 0 || this.allMaisonsGenerated();
    }
    return this.allMaisonsGenerated();
  }

  // === MÉTHODES POUR TYPE B (BÂTIMENTS) ===

  addBatiment() {
    if (!this.newBuildingLibelle || !this.newBuildingNiveau) {
      this.toast('Veuillez remplir tous les champs requis', 'Erreur', 'error');
      return;
    }

    const existingBatiment = this.batiments.find(bat => bat.libelle.toLowerCase() === this.newBuildingLibelle.toLowerCase());
    if (existingBatiment) {
      this.toast('Un bâtiment avec ce libellé existe déjà', 'Erreur', 'error');
      return;
    }

    // Créer les niveaux automatiquement
    const niveaux: NiveauBatiment[] = [];
    for (let i = 1; i <= this.newBuildingNiveau; i++) {
      niveaux.push({
        numero: i,
        maisonTypes: [],
        expanded: i === 1, // Seul le premier niveau est ouvert par défaut
        newMaisonCount: 1,
        newMaisonSuperficie: 0,
        newMaisonMontant: 0,
        newMaisonBaseActualisation: 0 // NOUVEAU CHAMP
      });
    }

    const newBatiment: Batiment = {
      libelle: this.newBuildingLibelle,
      niveau: this.newBuildingNiveau,
      niveaux: niveaux,
      generated: false,
      homes: [], // Initialisation du tableau des maisons
      expanded: true // Ouvert par défaut
    };

    this.batiments.push(newBatiment);
    this.resetNewBatimentForm();
    this.toast('Bâtiment ajouté avec succès', 'Succès', 'success');
  }

  removeBatiment(index: number) {
    Swal.fire({
      title: 'Confirmation',
      text: 'Êtes-vous sûr de vouloir supprimer ce bâtiment ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.batiments.splice(index, 1);
        this.regenerateBuildings();
        this.toast('Bâtiment supprimé', 'Succès', 'success');
      }
    });
  }

  toggleBatimentExpansion(batimentIndex: number) {
    const batiment = this.batiments[batimentIndex];
    if (batiment) {
      batiment.expanded = !batiment.expanded;
    }
  }

  toggleNiveauExpansion(batimentIndex: number, niveauIndex: number) {
    const batiment = this.batiments[batimentIndex];
    if (batiment && batiment.niveaux[niveauIndex]) {
      const niveau = batiment.niveaux[niveauIndex];
      niveau.expanded = !niveau.expanded;
    }
  }

  setNewMaisonTypeForNiveau(uuid: string, batimentIndex: number, niveauIndex: number) {
    const batiment = this.batiments[batimentIndex];
    if (batiment && batiment.niveaux[niveauIndex]) {
      const niveau = batiment.niveaux[niveauIndex];
      niveau.newMaisonTypeUuid = uuid;
      
      // Charger les détails du type sélectionné
      this.loadHomeTypeDetails(uuid).then(homeType => {
        if (homeType) {
          niveau.newMaisonTypeLabel = homeType.libelle || homeType.name;
        }
      });
    }
  }

  // MISE À JOUR : Ajout de la validation du champ baseActualisation
  addMaisonToNiveau(batimentIndex: number, niveauIndex: number) {
    const batiment = this.batiments[batimentIndex];
    if (!batiment) return;

    const niveau = batiment.niveaux[niveauIndex];
    if (!niveau) return;

    if (!niveau.newMaisonTypeUuid || !niveau.newMaisonCount || !niveau.newMaisonSuperficie || !niveau.newMaisonMontant || !niveau.newMaisonBaseActualisation) {
      this.toast('Veuillez remplir tous les champs requis', 'Erreur', 'error');
      return;
    }

    // Vérifier si le type n'existe pas déjà dans ce niveau
    const existingType = this.homeTypes.find(type => type.uuid === niveau.newMaisonTypeUuid);

    const newMaisonType: MaisonTypeNiveau = {
      typeUuid: niveau.newMaisonTypeUuid,
      typeLabel: existingType.libelle,
      count: niveau.newMaisonCount,
      superficie: niveau.newMaisonSuperficie,
      montant: niveau.newMaisonMontant,
      baseActualisation: niveau.newMaisonBaseActualisation // NOUVEAU CHAMP
    };

    niveau.maisonTypes.push(newMaisonType);
    
    // Reset des champs temporaires
    niveau.newMaisonTypeUuid = '';
    niveau.newMaisonTypeLabel = '';
    niveau.newMaisonCount = 1;
    niveau.newMaisonSuperficie = 0;
    niveau.newMaisonMontant = 0;
    niveau.newMaisonBaseActualisation = 0; // NOUVEAU CHAMP
    
    // Marquer le bâtiment comme non généré
    batiment.generated = false;
    batiment.homes = [];

    this.toast('Type de maison ajouté au niveau', 'Succès', 'success');
  }

  removeMaisonFromNiveau(batimentIndex: number, niveauIndex: number, maisonTypeIndex: number) {
    const batiment = this.batiments[batimentIndex];
    if (batiment && batiment.niveaux[niveauIndex]) {
      const niveau = batiment.niveaux[niveauIndex];
      niveau.maisonTypes.splice(maisonTypeIndex, 1);
      batiment.generated = false;
      batiment.homes = [];
      this.toast('Type de maison supprimé du niveau', 'Succès', 'success');
    }
  }

  // === MÉTHODE MISE À JOUR POUR LE FORMATAGE SIMPLE ===
  generateBatimentMaisons(batimentIndex: number) {
    const batiment = this.batiments[batimentIndex];
    if (!batiment || !this.canGenerateBatiment(batiment)) return;

    batiment.homes = [];
    let numeroSequentiel = 1; // Compteur global pour tout le bâtiment
    
    // Générer les maisons pour chaque niveau
    batiment.niveaux.forEach(niveau => {
      niveau.maisonTypes.forEach(maisonType => {
        for (let i = 0; i < maisonType.count; i++) {
          // Formatage simple : Libellé + A + numéro séquentiel
          const numeroFormate = `${batiment.libelle}-A${numeroSequentiel}`;
          
          const generatedHome: GeneratedHome = {
            numero: numeroFormate,
            niveau: niveau.numero,
            typeUuid: maisonType.typeUuid,
            typeLabel: maisonType.typeLabel,
            superficie: maisonType.superficie,
            montant: maisonType.montant,
            baseActualisation: maisonType.baseActualisation, // NOUVEAU CHAMP
            editable: false
          };
          batiment.homes.push(generatedHome);
          numeroSequentiel++; // Incrémenter le compteur global
        }
      });
    });

    // Générer les entrées dans le FormArray buildings selon la nouvelle structure
    this.generateBuildingFormEntries(batiment, batimentIndex);
    
    batiment.generated = true;
    const totalMaisons = batiment.homes.length;
    this.toast(`${totalMaisons} maisons générées pour ${batiment.libelle}`, 'Succès', 'success');
  }

  // MISE À JOUR : Ajout du champ baseActualisation dans le FormGroup
  generateBuildingFormEntries(batiment: Batiment, batimentIndex: number) {
    // Créer un FormGroup pour le bâtiment avec ses maisons
    const buildingFormGroup = this.formBuild.group({
      libelle: [batiment.libelle, [Validators.required]],
      niveau: [batiment.niveau, [Validators.required]],
      homes: this.formBuild.array([]) // Array pour les maisons de ce bâtiment
    });

    // Ajouter les maisons dans le FormArray du bâtiment
    const homesArray = buildingFormGroup.get('homes') as FormArray;
    batiment.homes.forEach(home => {
      const homeFormGroup = this.formBuild.group({
        uuid: [home.uuid || null],
        numero: [home.numero, [Validators.required]],
        niveau: [home.niveau, [Validators.required]],
        type: [home.typeUuid, [Validators.required]],
        superficie: [home.superficie, [Validators.required]],
        montant: [home.montant, [Validators.required]],
        baseActualisation: [home.baseActualisation, [Validators.required]], // NOUVEAU CHAMP
        ilot: [home.niveau.toString()], // Niveau comme ilot pour compatibilité backend
        lot: [home.numero] // Numéro comme lot pour compatibilité backend
      });
      homesArray.push(homeFormGroup);
    });

    // Ajouter le bâtiment au FormArray principal
    this.building.push(buildingFormGroup);
  }

  regenerateBuildings() {
    this.building.clear();
    this.batiments.forEach(batiment => {
      batiment.generated = false;
      batiment.homes = [];
    });
  }

  allBatimentsGenerated(): boolean {
    return this.batiments.length > 0 && this.batiments.every(bat => bat.generated);
  }

  // Méthode pour vérifier si on peut procéder depuis l'étape bâtiments (TYPE_C)
  canProceedFromBatiments(): boolean {
    if (this.f.type.value === 'TYPE_C') {
      // Pour TYPE_C, on peut continuer même sans bâtiments si on a des maisons simples
      return this.batiments.length === 0 || this.allBatimentsGenerated();
    }
    return this.allBatimentsGenerated();
  }

  // Méthode pour vérifier si TYPE_C a au moins quelque chose de configuré
  hasContentForTypeC(): boolean {
    if (this.f.type.value !== 'TYPE_C') return true;
    
    const hasMaisonsSimples = this.maisonTypes.length > 0;
    const hasBatiments = this.batiments.length > 0;
    
    return hasMaisonsSimples || hasBatiments;
  }

  canGenerateBatiment(batiment: Batiment): boolean {
    return batiment.niveaux.some(niveau => niveau.maisonTypes.length > 0);
  }

  getTotalMaisonsNiveau(niveau: NiveauBatiment): number {
    return niveau.maisonTypes.reduce((total, type) => total + type.count, 0);
  }

  getTotalMaisonsBatiment(batiment: Batiment): number {
    return batiment.niveaux.reduce((total, niveau) => {
      return total + this.getTotalMaisonsNiveau(niveau);
    }, 0);
  }

  // === MÉTHODES POUR LA MISE À JOUR DES CHAMPS ÉDITABLES ===

  // Méthodes pour les maisons simples (TYPE_A et TYPE_C) - MISE À JOUR
  updateSimpleLot(index: number, event: any) {
    const newValue = event.target.value;
    const homeControl = this.home.at(index) as FormGroup;
    homeControl.patchValue({
      lot: parseInt(newValue) || 0
    });
  }

  updateSimpleIlot(index: number, event: any) {
    const newValue = event.target.value;
    const homeControl = this.home.at(index) as FormGroup;
    homeControl.patchValue({
      ilot: parseInt(newValue) || 0
    });
  }

  updateSimpleSuperficie(index: number, event: any) {
    const newValue = event.target.value;
    const homeControl = this.home.at(index) as FormGroup;
    homeControl.patchValue({
      superficie: parseFloat(newValue) || 0
    });
  }

  updateSimpleMontant(index: number, event: any) {
    const newValue = event.target.value;
    const homeControl = this.home.at(index) as FormGroup;
    homeControl.patchValue({
      montant: parseFloat(newValue) || 0
    });
  }

  // NOUVELLE MÉTHODE : Mise à jour base actualisation maisons simples
  updateSimpleBaseActualisation(index: number, event: any) {
    const newValue = event.target.value;
    const homeControl = this.home.at(index) as FormGroup;
    homeControl.patchValue({
      baseActualisation: parseFloat(newValue) || 0
    });
  }

  // Méthodes pour les maisons de bâtiments (TYPE_B et TYPE_C)
  updateBatimentHomeSuperficie(batimentIndex: number, homeIndex: number, event: any) {
    const newValue = event.target.value;
    const batiment = this.batiments[batimentIndex];
    
    if (batiment && batiment.homes[homeIndex]) {
      batiment.homes[homeIndex].superficie = parseFloat(newValue) || 0;
      
      // Mettre à jour le FormArray correspondant si il existe
      if (this.building.at(batimentIndex)) {
        const buildingFormGroup = this.building.at(batimentIndex) as FormGroup;
        const homesArray = buildingFormGroup.get('homes') as FormArray;
        if (homesArray && homesArray.at(homeIndex)) {
          const homeFormGroup = homesArray.at(homeIndex) as FormGroup;
          homeFormGroup.patchValue({
            superficie: parseFloat(newValue) || 0
          });
        }
      }
    }
  }

  updateBatimentHomeMontant(batimentIndex: number, homeIndex: number, event: any) {
    const newValue = event.target.value;
    const batiment = this.batiments[batimentIndex];
    
    if (batiment && batiment.homes[homeIndex]) {
      batiment.homes[homeIndex].montant = parseFloat(newValue) || 0;
      
      // Mettre à jour le FormArray correspondant si il existe
      if (this.building.at(batimentIndex)) {
        const buildingFormGroup = this.building.at(batimentIndex) as FormGroup;
        const homesArray = buildingFormGroup.get('homes') as FormArray;
        if (homesArray && homesArray.at(homeIndex)) {
          const homeFormGroup = homesArray.at(homeIndex) as FormGroup;
          homeFormGroup.patchValue({
            montant: parseFloat(newValue) || 0
          });
        }
      }
    }
  }

  // NOUVELLE MÉTHODE : Mise à jour base actualisation bâtiments
  updateBatimentHomeBaseActualisation(batimentIndex: number, homeIndex: number, event: any) {
    const newValue = event.target.value;
    const batiment = this.batiments[batimentIndex];
    
    if (batiment && batiment.homes[homeIndex]) {
      batiment.homes[homeIndex].baseActualisation = parseFloat(newValue) || 0;
      
      // Mettre à jour le FormArray correspondant si il existe
      if (this.building.at(batimentIndex)) {
        const buildingFormGroup = this.building.at(batimentIndex) as FormGroup;
        const homesArray = buildingFormGroup.get('homes') as FormArray;
        if (homesArray && homesArray.at(homeIndex)) {
          const homeFormGroup = homesArray.at(homeIndex) as FormGroup;
          homeFormGroup.patchValue({
            baseActualisation: parseFloat(newValue) || 0
          });
        }
      }
    }
  }

  // Méthode updateBatimentHomeNumero mise à jour
  updateBatimentHomeNumero(batimentIndex: number, homeIndex: number, event: any) {
    const newValue = event.target.value;
    const batiment = this.batiments[batimentIndex];
    
    if (batiment && batiment.homes[homeIndex]) {
      // Validation du format (optionnel)
      if (!this.validateBatimentHomeNumber(newValue, batiment.libelle)) {
        this.toast(`Le numéro doit suivre le format ${batiment.libelle}-AXX`, 'Attention', 'warning');
      }
      
      // Vérifier l'unicité du numéro dans le bâtiment
      const duplicateIndex = batiment.homes.findIndex((home, index) => 
        index !== homeIndex && home.numero === newValue
      );
      
      if (duplicateIndex !== -1) {
        this.toast('Ce numéro existe déjà dans ce bâtiment', 'Erreur', 'error');
        return;
      }
      
      batiment.homes[homeIndex].numero = newValue;
      
      // Mettre à jour le FormArray correspondant si il existe
      if (this.building.at(batimentIndex)) {
        const buildingFormGroup = this.building.at(batimentIndex) as FormGroup;
        const homesArray = buildingFormGroup.get('homes') as FormArray;
        if (homesArray && homesArray.at(homeIndex)) {
          const homeFormGroup = homesArray.at(homeIndex) as FormGroup;
          homeFormGroup.patchValue({
            numero: newValue,
            lot: newValue // Maintenir la cohérence avec lot
          });
        }
      }
    }
  }

  // === MÉTHODES POUR LA RÉGÉNÉRATION DES NUMÉROS ===

  // Méthode pour régénérer seulement les numéros avec le nouveau format
  regenerateBatimentNumbers(batimentIndex: number) {
    const batiment = this.batiments[batimentIndex];
    if (!batiment || !batiment.generated) return;
    
    let numeroSequentiel = 1;
    
    // Mettre à jour les numéros existants avec le format simple
    batiment.homes.forEach(home => {
      home.numero = `${batiment.libelle}-A${numeroSequentiel}`;
      numeroSequentiel++;
    });
    
    // Mettre à jour le FormArray correspondant
    if (this.building.at(batimentIndex)) {
      const buildingFormGroup = this.building.at(batimentIndex) as FormGroup;
      const homesArray = buildingFormGroup.get('homes') as FormArray;
      
      if (homesArray) {
        batiment.homes.forEach((home, homeIndex) => {
          if (homesArray.at(homeIndex)) {
            const homeFormGroup = homesArray.at(homeIndex) as FormGroup;
            homeFormGroup.patchValue({
              numero: home.numero,
              lot: home.numero
            });
          }
        });
      }
    }
    
    this.toast('Numéros des maisons mis à jour', 'Succès', 'success');
  }

  // Méthode pour valider le format du numéro saisi manuellement (format simplifié)
  validateBatimentHomeNumber(numero: string, batimentLibelle: string): boolean {
    const pattern = new RegExp(`^${batimentLibelle}-A\\d+$`);
    return pattern.test(numero);
  }

  // Méthode pour mettre à jour le libellé du bâtiment
  updateBatimentLibelle(batimentIndex: number, newLibelle: string) {
    const batiment = this.batiments[batimentIndex];
    if (!batiment) return;
    
    const oldLibelle = batiment.libelle;
    batiment.libelle = newLibelle;
    
    // Si des maisons ont déjà été générées, proposer de les régénérer
    if (batiment.generated && batiment.homes.length > 0) {
      Swal.fire({
        title: 'Mettre à jour les numéros ?',
        text: 'Le libellé du bâtiment a changé. Souhaitez-vous mettre à jour automatiquement les numéros des maisons ?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, mettre à jour',
        cancelButtonText: 'Non, garder les numéros actuels'
      }).then((result) => {
        if (result.isConfirmed) {
          this.regenerateBatimentNumbers(batimentIndex);
        }
      });
    }
  }

  // Méthode pour récupérer les détails d'un type de maison
  loadHomeTypeDetails(typeUuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!typeUuid) {
        resolve(null);
        return;
      }
      
      // Vérifier d'abord dans le cache local
      const cachedType = this.homeTypes.find(type => type.uuid === typeUuid);
      if (cachedType) {
        resolve(cachedType);
        return;
      }

      // Sinon appeler le service pour récupérer les détails
      this.homeTypeService.getSingle(typeUuid).subscribe(
        (homeType) => {
          // Ajouter au cache local pour éviter les appels répétés
          this.homeTypes.push(homeType);
          resolve(homeType);
        },
        (error) => {
          console.error('Erreur lors du chargement des détails du type:', error);
          reject(error);
        }
      );
    });
  }

  // Méthode pour obtenir le label du type (synchrone, utilise le cache)
  getTypeLabelSync(typeUuid: string): string {
    const type = this.homeTypes.find(t => t.uuid === typeUuid);
    return type ? type.libelle || type.name || 'Type inconnu' : 'Type inconnu';
  }

  // Méthode principale pour obtenir le label du type
  getTypeLabel(typeUuid: string): string {
    return this.getTypeLabelSync(typeUuid);
  }

  // === MÉTHODES POUR LES STATISTIQUES ===

  getTotalGeneral(): number {
    const totalSimples = this.getTotalMaisons();
    const totalBatiments = this.batiments.reduce((total, bat) => total + bat.homes.length, 0);
    return totalSimples + totalBatiments;
  }

  getBatimentsTotalHomes(): number {
    return this.batiments.reduce((total, bat) => total + bat.homes.length, 0);
  }

  // === MÉTHODES UTILITAIRES ===

  // MISE À JOUR : Reset du nouveau champ
  resetNewMaisonTypeForm() {
    this.newTypeUuid = '';
    this.newTypeLabel = '';
    this.newTypeCount = 1;
    this.newTypeSuperficie = 0;
    this.newTypeMontant = 0;
    this.newTypeBaseActualisation = 0; // NOUVEAU CHAMP
  }

  resetNewBatimentForm() {
    this.newBuildingLibelle = '';
    this.newBuildingNiveau = 1;
  }

  resetData() {
    this.maisonTypes = [];
    this.batiments = [];
    this.home.clear();
    this.building.clear();
    // Reset des paramètres de configuration
    this.radicalCode = '';
    this.startNumber = 1;
    this.defaultIlot = 1;
    this.numberingMode = 'continue';
  }

  // Méthode pour valider et formater le radical
  validateRadicalCode(radical: string): boolean {
    // Le radical peut contenir des lettres et des chiffres, mais pas d'espaces au milieu
    const pattern = /^[A-Za-z0-9]+$/;
    return radical === '' || pattern.test(radical.trim());
  }

  // Méthode pour prévisualiser le format des lots
  getPreviewLotFormat(): string {
    const nextNumber = this.getNextAvailableLotNumber();
    if (this.radicalCode && this.radicalCode.trim() !== '') {
      return `${this.radicalCode.trim()}${nextNumber}, ${this.radicalCode.trim()}${nextNumber + 1}, ...`;
    } else {
      return `${nextNumber}, ${nextNumber + 1}, ${nextNumber + 2}, ...`;
    }
  }

  // Méthode pour régénérer tous les numéros de lots avec les nouveaux paramètres
  regenerateAllLotNumbers() {
    if (this.home.length === 0) {
      this.toast('Aucune maison à régénérer', 'Information', 'info');
      return;
    }

    Swal.fire({
      title: 'Régénérer les numéros ?',
      text: 'Cette action va recalculer tous les numéros de lots avec les nouveaux paramètres. Continuer ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, régénérer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        // Régénérer tous les numéros
        for (let i = 0; i < this.home.length; i++) {
          const homeControl = this.home.at(i) as FormGroup;
          const lotNumber = this.startNumber + i;
          
          // Génération du numéro de lot avec ou sans radical
          let lotFormatted: string;
          if (this.radicalCode && this.radicalCode.trim() !== '') {
            lotFormatted = `${this.radicalCode.trim()}${lotNumber}`;
          } else {
            lotFormatted = lotNumber.toString();
          }
          
          // Détermination de l'ilot
          let ilotValue: number;
          if (this.radicalCode && this.radicalCode.trim() !== '') {
            ilotValue = lotNumber;
          } else {
            ilotValue = this.defaultIlot;
          }
          
          homeControl.patchValue({
            lot: lotFormatted,
            ilot: ilotValue
          });
        }
        
        this.toast('Numéros de lots régénérés avec succès', 'Succès', 'success');
      }
    });
  }
  
  setOwnerUuid(uuid: string) {
    if (uuid) {
      this.f.owner.setValue(uuid);
    } else {
      this.f.owner.setValue(null);
    }
  }

  setHomeTypeUuid(uuid: string, row: any) {
    if (uuid) {
      row.get('type').setValue(uuid);
    } else {
      row.get('type').setValue(null);
    }
  }

  onChangeLoad(type: string) {
    this.activeTab = type;
  }

  onSubmit() {
    this.submit = true;
    if (this.form.valid) {
      // Préparer les données selon le format attendu par votre backend
      const formData = this.form.getRawValue();
      
      // Ajouter les données spécifiques selon le type
      if (this.f.type.value === 'TYPE_A') {
        // Pour TYPE_A, les maisons sont directement liées à la promotion
        formData.homes = this.home.getRawValue();
        formData.buildings = []; // Pas de buildings pour TYPE_A
        
      } else if (this.f.type.value === 'TYPE_B') {
        // Pour TYPE_B, structure hiérarchique avec buildings contenant leurs homes
        formData.buildings = this.building.getRawValue();
        
        // Aplatir les homes pour le backend si nécessaire
        formData.homes = [];
        this.building.controls.forEach((buildingControl: any) => {
          const buildingData = buildingControl.getRawValue();
          if (buildingData.homes && buildingData.homes.length > 0) {
            buildingData.homes.forEach((home: any) => {
              formData.homes.push({
                ...home,
                building: buildingData.libelle // Référence au bâtiment parent
              });
            });
          }
        });
        
      } else if (this.f.type.value === 'TYPE_C') {
        // Pour TYPE_C, combinaison de maisons simples ET de bâtiments
        formData.buildings = this.building.getRawValue();
        
        // Commencer avec les maisons simples
        formData.homes = this.home.getRawValue();
        
        // Ajouter les maisons des bâtiments
        this.building.controls.forEach((buildingControl: any) => {
          const buildingData = buildingControl.getRawValue();
          if (buildingData.homes && buildingData.homes.length > 0) {
            buildingData.homes.forEach((home: any) => {
              formData.homes.push({
                ...home,
                building: buildingData.libelle // Référence au bâtiment parent
              });
            });
          }
        });
      }
      
      // Données des formulaires annexes
      formData.piece = this.pieceForm.getRawValue();
      formData.equipement = this.equipementForm.getRawValue();
      formData.video = this.videoForm.getRawValue();
      formData.gallery = this.galleryForm.getRawValue();
      formData.plan = this.planForm.getRawValue();
      
      console.log('Données envoyées:', formData); // Pour débuggage
      
      this.promotionService.add(formData).subscribe(res => {
        if (res?.status === 'success') {
          this.modal.close('ferme');
          if (formData.uuid) {
            this.emitter.emit({ action: 'PROMOTION_UPDATED', payload: res?.data });
          } else {
            this.emitter.emit({ action: 'PROMOTION_ADD', payload: res?.data });
          }
        }
      }, error => { 
        console.error('Erreur lors de la soumission:', error);
      });
    } else {
      console.log('Formulaire invalide:', this.form.errors);
      return;
    }
  }

  onConfirme() {
    Swal.fire({
      title: '',
      text: 'Confirmez-vous l\'enregistrement ?',
      icon: 'warning',
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: 'Confirmer <i class="fas fa-check"></i>',
      cancelButtonText: 'Annuler <i class="feather icon-x-circle"></i>',
      confirmButtonColor: '#1bc943',
      reverseButtons: true
    }).then((willDelete) => {
      if (!willDelete.dismiss) {
        this.onSubmit();
      }
    });
  }

  loadfile(data: any) {
    if (data && data !== null) {
      const file = data.todo.file;
      this.file.push(
        this.formBuild.group({
          uniqId: [data.todo.uniqId, [Validators.required]],
          fileName: [file.name, [Validators.required]],
          fileSize: [file.size, [Validators.required]],
          fileType: [file.type, [Validators.required]],
          loaded: [data.todo.loaded, [Validators.required]],
          chunk: [data.chunk, [Validators.required]],
        })
      );
    }
  }

  files(data: any) {
    if (data && data !== null) {
      data.forEach((item: any) => {
        this.folder.push(
          this.formBuild.group({
            uuid: [item?.uuid, [Validators.required]],
            name: [item?.name],
            path: [item?.path]
          })
        );
      });
    }
  }

  uploadFile(data: any, type: string) {
    if (type === "GALLERY") {
      this.galleryForm.get('files').setValue(data);
    }
    if (type === "PLAN") {
      this.planForm.get('files').setValue(data);
    }
  }

  upload(files: any) {
    for (const file of files) {
      this.uploadService.upload(file);
    }
  }

  setParam(property: string, value: any) {
    if (value) {
      if (this.form.value.hasOwnProperty(property)) {
        Object.assign(this.form.value, { [property]: value });
      }
      if (this.form.controls.hasOwnProperty(property)) {
        this.form.controls[property].setValue(value);
      }
    }
  }

  @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (event.keyCode === this.ESCAPE_KEYCODE) {
      this.onClose();
    }
  }

  onClose() {
    if (!this.edit && this.form.value.folderUuid) {
      var data = { uuid: this.form.value.folderUuid, path: 'promotion' };
      this.uploadService.getDelete(data).subscribe((res: any) => {
        if (res) {
          if (res?.status === 'success') {
            this.form.reset();
            this.modal.close('ferme');
          }
        }
        return res;
      });
    } else {
      this.form.reset();
      this.modal.close('ferme');
    }
  }

  onReset() {
    if (this.form.value.folderUuid) {
      this.toast('Impossible de vider le formulaire lorsque un upload a été effectué', 'Une erreur a été rencontrée', 'warning');
    } else {
      this.form.reset();
      this.form.controls['folderUuid'].setValue(null);
    }
  }

  updateGeo(event: any): void {
    const lat = event.coords.lat;
    const lng = event.coords.lng;
    this.lat = lat;
    this.lng = lng;
    this.form.controls.lat.setValue(event.coords.lat);
    this.form.controls.lng.setValue(event.coords.lng);
  }

  updateZoom(event: any): void {
    this.form.controls.zoom.setValue(event);
  }

  toast(msg: string, title: string, type: string) {
    if (type == 'info') {
      this.toastr.info(msg, title);
    } else if (type == 'success') {
      this.toastr.success(msg, title);
    } else if (type == 'warning') {
      this.toastr.warning(msg, title);
    } else if (type == 'error') {
      this.toastr.error(msg, title);
    }
  }

  // Getters
  get f() { return this.form.controls; }
  get home() { return this.form.get('homes') as FormArray; }
  get building() { return this.form.get('buildings') as FormArray; }
  get file() { return this.form.get('files') as FormArray; }
  get folder() { return this.form.get('folders') as FormArray; }
}